import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Seo } from '@/seo/Seo';
import { breadcrumbSchema, faqSchema } from '@/seo/schemas';
import { SeoPage } from '@/components/seo/SeoLayout';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { SlotPicker, type SlotSelection } from '@/components/consultation/SlotPicker';
import { ConsultationConfirmed } from '@/components/consultation/ConsultationConfirmed';
import { useConsultationCheckout } from '@/hooks/useConsultationCheckout';
import { consultationsApi, CONSULTATION_SERVICE_SLUG } from '@/api/consultationsApi';
import { ConsultationReviews } from '@/components/consultation/ConsultationReviews';
import { CouponSection } from '@/components/booking/CouponSection';
import { useCoupon } from '@/hooks/useCoupon';
import { formatCurrency, spokenTime } from '@/utils/formatters';

export const CONSULTATION_PATH = '/online-physiotherapy-consultation';

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
               'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Wed 23 Sep" — the date a person reads, not the one we store. */
function readableDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  const when = new Date(Date.UTC(y, m - 1, d));
  return `${WEEKDAY[when.getUTCDay()]} ${d} ${MONTH[m - 1]}`;
}

interface PublicConfig {
  available: boolean;
  coupons_enabled?: boolean;
  meet_provider?: string;
  price: number;
  session_minutes: number;
  title: string;
  blurb: string;
}

/** Mirrors the API's NOTES_MIN_CHARS: the match is made on this text. */
const NOTES_MIN_CHARS = 10;

const PLATFORM_NAMES: Record<string, string> = { google: 'Google Meet', jitsi: 'Jitsi Meet' };

interface Faq {
  question: string;
  answer: string;
  /** Authoritative outbound reference shown after the answer. */
  source?: { label: string; href: string };
}

function buildFaqs(provider?: string): Faq[] {
  const platform = PLATFORM_NAMES[provider ?? ''] ?? 'a secure video call';
  return [
    {
      question: 'Where does the consultation happen?',
      answer: `On ${platform}, in your browser or the app. There is nothing to install: the joining link we send opens the call on a phone or a laptop.`,
    },
    {
      question: 'How do I join, and where is my link?',
      answer:
        'As soon as your payment is confirmed we email you the joining link for your scheduled time. The same link is always available under History in your Recovry account, so a lost email never costs you the session.',
    },
    ...FAQS,
  ];
}

const FAQS: Faq[] = [
  {
    question: 'What happens in an online physiotherapy consultation?',
    answer:
      'A qualified physiotherapist talks through your symptoms, how they started and what makes them worse, then guides you through simple movement checks on camera. You finish with an explanation of what is likely going on and what to do next, including whether you need hands-on treatment at a clinic.',
  },
  {
    question: 'Is an online consultation as good as seeing someone in person?',
    answer:
      'For assessment, advice and exercise guidance it works well, and research on telerehabilitation shows comparable outcomes for many musculoskeletal problems. It cannot replace hands-on treatment such as manual therapy or dry needling. If you need those, your physiotherapist will say so and can point you to a verified clinic near you.',
    source: { label: 'NHS: physiotherapy', href: 'https://www.nhs.uk/conditions/physiotherapy/' },
  },
  {
    question: 'What do I need to join?',
    answer:
      'A phone or laptop with a camera, a stable internet connection and a few feet of space to move in. Wear clothing that lets you move the affected area. Joining is a single tap on the link we email you, with nothing to install.',
  },
  {
    question: 'Can I cancel or move my appointment?',
    answer:
      'Yes. You can move it once free of charge with enough notice, and cancelling more than 24 hours ahead is refunded in full. Between 12 and 24 hours you are refunded half, and inside 12 hours the session is not refunded because the physiotherapist has kept the time for you.',
  },
  {
    question: 'Will I get a prescription or a scan referral?',
    answer:
      'Physiotherapists do not prescribe medication. If your physiotherapist thinks you need imaging or a medical opinion, they will tell you clearly and recommend seeing a doctor.',
  },
  {
    question: 'Is my consultation private?',
    answer:
      'Yes. The video room is one-time and unlisted, only you and your physiotherapist are invited, and we do not record sessions.',
  },
];

/** One line of the slip. Empty rows stay as rules, so the card reads as a form
 *  waiting to be filled rather than a panel telling you what to do. */
function SlipRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-dashed py-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd
        className={
          value
            ? 'text-right font-medium tabular-nums text-foreground'
            : 'h-px w-24 self-center bg-border'
        }
      >
        {value || <span className="sr-only">not chosen yet</span>}
      </dd>
    </div>
  );
}

export default function OnlineConsultation() {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [searchParams] = useSearchParams();
  // Coming back from login: the slot rides in the URL, so read it once at mount.
  const [selected, setSelected] = useState<SlotSelection | null>(() => {
    const date = searchParams.get('date');
    const time = searchParams.get('time');
    // Format it the same way the picker does, or the slip shows a raw "08:00".
    return date && time ? { date, time, display: spokenTime(time) } : null;
  });
  const [reason, setReason] = useState('');
  const { stage, booking, start, busy } = useConsultationCheckout();

  useEffect(() => {
    let cancelled = false;
    consultationsApi
      .getConfig()
      .then((configRes) => {
        if (cancelled) return;
        setConfig(configRes.data.consultation as PublicConfig);
      })
      .catch(() => {
        if (!cancelled) setConfig({ available: false } as PublicConfig);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const available = !!config?.available;
  const price = config?.price ?? 0;
  const minutes = config?.session_minutes ?? 30;
  const coupon = useCoupon({ amount: price, serviceSlug: CONSULTATION_SERVICE_SLUG });
  const payable = Math.max(0, price - coupon.discount);
  const reasonOk = reason.trim().length >= NOTES_MIN_CHARS;
  const faqs = buildFaqs(config?.meet_provider);

  // The chat launcher moves above the mobile booking bar while it is showing.
  useEffect(() => {
    const showBar = available && !!selected;
    document.body.classList.toggle('has-sticky-bar', showBar);
    return () => document.body.classList.remove('has-sticky-bar');
  }, [available, selected]);

  const startBooking = () => {
    if (!selected) return;
    if (!reasonOk) {
      toast.error('Tell us what you would like help with before booking.');
      return;
    }
    start(selected.date, selected.time, reason.trim(), coupon.codeForPayment);
  };

  // Lives next to the fee, because changing the fee is the only thing it does.
  // Rendered twice: in the sticky slip on desktop, inline on narrow screens.
  const couponBlock = config?.coupons_enabled ? (
    <CouponSection
      compact
      collapsible
      couponCode={coupon.code}
      onCodeChange={coupon.setCode}
      onApply={coupon.apply}
      onRemove={coupon.remove}
      applied={coupon.applied}
      discount={coupon.discount}
      validating={coupon.validating}
      availableCoupons={coupon.available}
      appOnly={coupon.appOnly}
    />
  ) : null;

  if (stage === 'done' && booking) {
    return (
      <SeoPage>
        <ConsultationConfirmed booking={booking} />
      </SeoPage>
    );
  }

  const bookLabel =
    stage === 'holding'
      ? 'Holding your time'
      : stage === 'paying'
        ? 'Opening payment'
        : stage === 'confirming'
          ? 'Confirming'
          : 'Book this time';

  return (
    <SeoPage>
      <Seo
        title="Online Physiotherapy Consultation | Video Call | Recovry"
        description={`Talk to a qualified physiotherapist over video, from home. ${minutes}-minute session, assessment and a clear plan for what to do next. Book a time that suits you.`}
        canonical={CONSULTATION_PATH}
        robots={available ? undefined : 'noindex, follow'}
        keywords="online physiotherapy consultation, online physio consultation, video physiotherapy appointment, physiotherapist online india, telerehabilitation"
        schema={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Online consultation', path: CONSULTATION_PATH },
          ]),
          faqSchema(faqs.map(({ question, answer }) => ({ question, answer }))),
        ]}
      />

      <Breadcrumbs
        items={[
          { name: 'Home', path: '/' },
          { name: 'Online consultation', path: CONSULTATION_PATH },
        ]}
      />

      <header className="max-w-3xl">
        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          {config?.title || 'Online physiotherapy consultation'}
        </h1>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          {available ? (
            <>
              <span className="font-semibold tabular-nums text-foreground">
                {formatCurrency(price)}
              </span>{' '}
              for a {minutes}-minute video call with a qualified physiotherapist, who will tell
              you what is going on and what to do about it.
            </>
          ) : (
            config?.blurb ||
            'Speak to a qualified physiotherapist over video and get a clear answer on what is wrong and what to do about it.'
          )}
        </p>
      </header>

      {config === null ? (
        <Skeleton className="mt-12 h-64 w-full rounded-xl" data-loading />
      ) : !available ? (
        <div className="mt-12 border-t pt-10">
          <p className="text-lg font-medium text-foreground">Not open for booking yet.</p>
          <p className="mt-2 max-w-xl text-muted-foreground">
            We are lining up physiotherapists for video appointments. You can book an in-person
            session at a verified clinic in the meantime.
          </p>
          <Button className="mt-5" asChild>
            <a href="/centres">Find a clinic</a>
          </Button>
        </div>
      ) : (
        <div className="mt-12 grid gap-12 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0">
            <section aria-labelledby="pick-a-time">
              <h2 id="pick-a-time" className="sr-only">
                Pick a time
              </h2>
              <SlotPicker selected={selected} onSelect={setSelected} disabled={busy} />
            </section>

            {selected && (
              <section aria-labelledby="what-helps" className="mt-10 border-t pt-8">
                <h2
                  id="what-helps"
                  className="text-xl font-bold tracking-tight text-foreground sm:text-2xl"
                >
                  What would you like help with?
                  <span className="ml-3 inline-block translate-y-[-3px] rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 align-middle text-xs font-semibold text-primary">
                    Required
                  </span>
                </h2>
                <p className="mt-2 max-w-xl text-muted-foreground">
                  You are matched with whichever physiotherapist is free at the time you pick,
                  based on what issue you describe here. A sentence or two is enough; they read it before
                  the call so the time is spent on you.
                </p>
                <Textarea
                  className="mt-4 max-w-xl"
                  rows={3}
                  maxLength={1000}
                  required
                  aria-required="true"
                  aria-labelledby="what-helps"
                  value={reason}
                  disabled={busy}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Lower back pain for about three weeks, worse when I sit at my desk."
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  {reason.trim().length === 0
                    ? 'Required before you can book.'
                    : reasonOk
                      ? 'Thanks, that is enough to go on.'
                      : 'A few more words helps.'}
                </p>

              </section>
            )}

            {couponBlock && (
              <div className="mt-10 border-t pt-8 lg:hidden">
                <div className="max-w-xs">{couponBlock}</div>
              </div>
            )}

            <section aria-labelledby="reviews" className="mt-14 border-t pt-10">
              <h2 id="reviews" className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                What patients say
              </h2>
              <ConsultationReviews />
            </section>

            <section aria-labelledby="questions" className="mt-14 border-t pt-10 pb-28 lg:pb-0">
              <h2
                id="questions"
                className="text-xl font-bold tracking-tight text-foreground sm:text-2xl"
              >
                Before you book
              </h2>
              {/* Native disclosure: the answers stay in the DOM for the FAQ schema
                  and the prerender, and the page is a third as tall for a person. */}
              <div className="mt-6 divide-y">
                {faqs.map((faq) => (
                  <details key={faq.question} className="group py-4">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-foreground [&::-webkit-details-marker]:hidden">
                      {faq.question}
                      <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                    </summary>
                    <p className="mt-2 max-w-2xl leading-relaxed text-muted-foreground">
                      {faq.answer}
                      {faq.source && (
                        <>
                          {' '}
                          <a
                            href={faq.source.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {faq.source.label}
                          </a>
                        </>
                      )}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          </div>

          <aside className="hidden lg:sticky lg:top-24 lg:block lg:self-start">
            <div className="rounded-xl border bg-card p-6 shadow-sm">
              <p className="font-semibold text-foreground">Your appointment</p>
              <dl className="mt-3">
                <SlipRow label="Date" value={selected ? readableDate(selected.date) : undefined} />
                <SlipRow label="Time" value={selected?.display} />
                <SlipRow label="Length" value={`${minutes} minutes`} />
                <SlipRow label="Fee" value={formatCurrency(price)} />
              </dl>
              {couponBlock && <div className="mt-4">{couponBlock}</div>}
              {coupon.discount > 0 && (
                <p className="mt-4 flex items-baseline justify-between text-foreground">
                  <span className="text-sm text-muted-foreground">You pay</span>
                  <span className="text-xl font-semibold tabular-nums">
                    {formatCurrency(payable)}
                  </span>
                </p>
              )}

              <Button
                className="mt-6 w-full"
                size="lg"
                disabled={!selected || !reasonOk || busy}
                onClick={() => selected && startBooking()}
              >
                {busy && <Loader2 className="size-4 animate-spin" />}
                {bookLabel}
              </Button>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                Cancel more than 24 hours ahead for a full refund. Move it once for free.
              </p>
            </div>
          </aside>
        </div>
      )}

      {available && (
        <div
          className={`fixed inset-x-0 bottom-0 z-40 border-t bg-card px-4 py-3 lg:hidden ${
            selected ? '' : 'hidden'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold tabular-nums text-foreground">
                {selected?.display}
              </p>
              <p className="truncate text-sm text-muted-foreground">
                {selected ? readableDate(selected.date) : null}
              </p>
            </div>
            <Button
              size="lg"
              disabled={!selected || !reasonOk || busy}
              onClick={() => selected && startBooking()}
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              {busy ? 'Working' : `Book for ${formatCurrency(payable)}`}
            </Button>
          </div>
        </div>
      )}
    </SeoPage>
  );
}
